import { Dialog } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';

import { DialogConfig } from '../models';
import { MdwDialog } from './dialog/dialog';
import { DialogOptions } from '../models/dialog-options';

@Injectable({ providedIn: 'root' })
export class MdwDialogService {
  private cdkDialogService = inject(Dialog);

  private openDialogs: Set<ComponentType<unknown>> = new Set();

  public alert(message: string, title?: string, buttonText?: string) {
    this.confirm({
      title,
      message,
      primaryButton: buttonText || 'OK',
      showSecondaryButton: false,
      allowDismiss: false,
    });
  }

  public alertDanger(message: string, title?: string, buttonText?: string) {
    this.confirm({
      title,
      message,
      dangerButton: buttonText || 'OK',
      showSecondaryButton: false,
      allowDismiss: false,
    });
  }

  public async confirm(options: DialogConfig, onConfirm?: () => void, onCancel?: () => void) {
    const dialogRef = this.cdkDialogService.open(MdwDialog, {
      width: '472px',
      data: {
        ...options,
      },
      autoFocus: false,
    });

    const result = await firstValueFrom(dialogRef.closed);

    if (result) {
      onConfirm?.();
    } else {
      onCancel?.();
    }
  }

  public async open<T, D>(
    dialogComponent: ComponentType<T>,
    options: DialogOptions,
    onConfirm?: (result: D) => void,
    onCancel?: () => void,
    onComplete?: () => void,
  ) {
    if (this.openDialogs.has(dialogComponent)) {
      return;
    }

    this.openDialogs.add(dialogComponent);

    const dialogRef = this.cdkDialogService.open(dialogComponent, {
      panelClass: options.altPanelClass ? [options.altPanelClass] : [],
      width: options?.width || '480px',
      data: options?.data,
      height: options?.height,
      maxHeight: options?.maxHeight,
      minHeight: options?.minHeight,
      maxWidth: options?.maxWidth,
      minWidth: options?.minWidth,
      autoFocus: false,
    });

    const result = await firstValueFrom(dialogRef.closed as Observable<D>);

    if (result && onConfirm) {
      onConfirm(result);
    } else if (onCancel) {
      onCancel();
    }

    if (onComplete) {
      onComplete();
    }

    this.openDialogs.delete(dialogComponent);

    return dialogRef;
  }

  public async openSpotlight<T, D>(
    dialogComponent: ComponentType<T>,
    options: DialogOptions,
    onConfirm?: (result: D) => void,
    onCancel?: () => void,
    onComplete?: () => void,
  ) {
    if (this.openDialogs.has(dialogComponent)) {
      return;
    }

    this.openDialogs.add(dialogComponent);

    const data = options?.data ? { ...options.data, isSpotlight: true } : { isSpotlight: true };
    const dialogRef = this.cdkDialogService.open(dialogComponent, {
      backdropClass: 'spotlight-backdrop',
      panelClass: ['spotlight-dialog'],
      width: options?.width || '480px',
      data,
      height: options?.height,
      maxHeight: options?.maxHeight,
      minHeight: options?.minHeight,
      maxWidth: options?.maxWidth,
      minWidth: options?.minWidth,
      autoFocus: false,
    });

    const result = await firstValueFrom(dialogRef.closed as Observable<D>);

    if (result && onConfirm) {
      onConfirm(result);
    } else if (onCancel) {
      onCancel();
    }

    if (onComplete) {
      onComplete();
    }

    this.openDialogs.delete(dialogComponent);

    return dialogRef;
  }

  public async openBAM<T>(dialogComponent: ComponentType<T>, _data?: unknown) {
    if (this.openDialogs.has(dialogComponent)) {
      return;
    }

    this.openDialogs.add(dialogComponent);

    const data = _data ? { ..._data, isBAM: true } : { isBAM: true };

    const dialogRef = this.cdkDialogService.open(dialogComponent, {
      panelClass: ['bam-dialog'],
      data,
    });

    await firstValueFrom(dialogRef.closed);

    this.openDialogs.delete(dialogComponent);

    return dialogRef;
  }

  public async openFullPage<T, D>(
    dialogComponent: ComponentType<T>,
    _data?: unknown,
    onConfirm?: (result: D) => void,
    onCancel?: () => void,
    onComplete?: () => void,
  ) {
    if (this.openDialogs.has(dialogComponent)) {
      return;
    }
    this.openDialogs.add(dialogComponent);

    const data = _data ? { ..._data, isFullPage: true } : { isFullPage: true };

    const dialogRef = this.cdkDialogService.open(dialogComponent, {
      panelClass: ['full-page-dialog'],
      data,
    });

    const result = await firstValueFrom(dialogRef.closed as Observable<D>);

    if (result && onConfirm) {
      onConfirm(result);
    } else if (onCancel) {
      onCancel();
    }

    if (onComplete) {
      onComplete();
    }

    this.openDialogs.delete(dialogComponent);

    return dialogRef;
  }

  public async openPreview<T, D>(
    dialogComponent: ComponentType<T>,
    _data?: unknown,
    onConfirm?: (result: D) => void,
    onCancel?: () => void,
    onComplete?: () => void,
  ) {
    if (this.openDialogs.has(dialogComponent)) {
      return;
    }
    this.openDialogs.add(dialogComponent);

    const data = _data ? { ..._data, isPreview: true } : { isPreview: true };

    const dialogRef = this.cdkDialogService.open(dialogComponent, {
      panelClass: ['preview-dialog'],
      backdropClass: 'preview-backdrop',
      data,
    });
    const result = await firstValueFrom(dialogRef.closed as Observable<D>);

    if (result && onConfirm) {
      onConfirm(result);
    } else if (onCancel) {
      onCancel();
    }

    if (onComplete) {
      onComplete();
    }

    this.openDialogs.delete(dialogComponent);

    return dialogRef;
  }
}
